
class TextSplitter {
  static #span(className, ...content) {
    const span = document.createElement('span');
    span.className = className;
    span.append(...content);
    return span;
  }

  static #eachWord(el, wrap) {
    const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
    const texts = [];
    while (walker.nextNode()) texts.push(walker.currentNode);
    for (const text of texts) {
      const parts = text.data.split(/(\s+)/).filter(Boolean);
      text.replaceWith(...parts.map((part) => (/^\s/.test(part) ? part : wrap(part))));
    }
  }

  static splitWords(el) {
    const text = el.textContent.trim();
    if (!text) return;

    el.setAttribute('aria-label', text);
    let index = 0;
    this.#eachWord(el, (word) => {
      const inner = this.#span('word-inner', word);
      inner.style.setProperty('--word-index', index++);
      return this.#span('word', inner);
    });
    el.style.setProperty('--word-step', `${Math.min(60, 600 / (index - 1))}ms`);
    el.style.setProperty('--word-count', index);
    el.dataset.textSplit = 'words';
  }

  static processAll() {
    document.querySelectorAll('[data-animate="text-words"]:not([data-text-split]):not(.animated), [data-animate="text-lit"]:not([data-text-split])').forEach(el => this.splitWords(el));
  }
}

class AnimationController {
  #controller = new AbortController();
  #observer;
  #mutation;
  #visibilityObserver;
  #animated = new Set();
  #pendingDelays = new Map();

  constructor() {
    if (this.#prefersReduced()) {
      this.#disable();
      this.#watchReducedMotion();
      return;
    }

    this.#watchReducedMotion();
    this.#init();
  }

  get signal() {
    return this.#controller.signal;
  }

  #init() {
    const setup = () => {
      this.#endOpening();
      this.#revealOnScreen();
      document.body.classList.add('animations-ready');
      TextSplitter.processAll();
      this.#calculateStagger();
      this.#createObserver();
      this.#observeAll();
      this.#watchMutations();
      this.#watchDesignMode();
      this.#watchResize();
    };

    if ('requestIdleCallback' in window) {
      requestIdleCallback(setup, { timeout: 150 });
    } else {
      setTimeout(setup, 0);
    }
  }

  #endOpening() {
    const opening = document.getAnimations().filter((animation) => animation.animationName?.startsWith('opening-'));
    Promise.allSettled(opening.map((animation) => animation.finished))
      .then(() => document.documentElement.setAttribute('data-opening-played', ''));
  }

  #revealOnScreen() {
    const onScreen = [...document.querySelectorAll('[data-animate]:not([data-animate="shine"])')].filter((el) => {
      const box = el.getBoundingClientRect();
      return box.bottom > 0 && box.right > 0 && box.top < innerHeight && box.left < innerWidth;
    });
    for (const el of onScreen) el.classList.add('animated');
  }

  #createObserver() {
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) this.#animate(entry.target);
      }
    });
  }

  #observeAll() {
    if (!this.#observer) return;

    const els = document.querySelectorAll('[data-animate]:not(.animated):not([data-animate="none"])');
    for (const el of els) {
      if (el.offsetParent === null) continue;
      this.#observer.observe(el);
    }
  }

  #animate(el) {
    this.#observer?.unobserve(el);
    if (this.#animated.has(el) || el.classList.contains('animated')) return;
    this.#animated.add(el);

    let type = el.getAttribute('data-animate');

    if (type === 'inherit') {
      type = this.#resolveInherit(el);
      if (type && type !== 'none') {
        el.setAttribute('data-animate', type);
      } else {
        for (const figure of el.querySelectorAll('[data-count-up]')) this.#countUp(figure);
        el.classList.add('animated');
        return;
      }
    }

    const delay = parseInt(el.style.getPropertyValue('--animate-delay'), 10) || 0;

    if (delay > 0) {
      const timer = setTimeout(() => {
        this.#pendingDelays.delete(el);
        this.#trigger(el);
      }, delay);
      this.#pendingDelays.set(el, timer);
    } else {
      this.#trigger(el);
    }
  }

  #resolveInherit(el) {
    const container = el.closest('[data-container-animate]');
    if (container) {
      const containerType = container.dataset.containerAnimate;
      if (containerType && containerType !== 'none') return containerType;
      if (containerType === 'none') return 'none';
    }
    return window.Scale.settings.animations_preset;
  }

  #trigger(el) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (el.classList.contains('animated')) return;
        for (const figure of el.querySelectorAll('[data-count-up]')) this.#countUp(figure);
        el.classList.add('animated', 'animating');
        requestAnimationFrame(() => setTimeout(() => this.#settle(el)));
      });
    });
  }

  #settle(el) {
    const entrance = el.getAnimations({ subtree: true })
      .filter((animation) => animation.timeline === document.timeline && animation.effect?.getComputedTiming().endTime !== Infinity);
    Promise.allSettled(entrance.map((animation) => animation.finished))
      .then(() => el.classList.remove('animating'));
  }

  #countUp(el) {
    const text = el.textContent;
    const value = parseFloat(text);
    const decimals = text.split('.')[1]?.length ?? 0;
    const duration = parseFloat(getComputedStyle(el).getPropertyValue('--duration-slow')) * 2;
    const start = performance.now();
    el.style.minInlineSize = `${el.getBoundingClientRect().width}px`;
    const step = (now) => {
      const progress = Math.min(Math.max((now - start) / duration, 0), 1);
      el.textContent = progress < 1 ? (value * (1 - (1 - progress) ** 3)).toFixed(decimals) : text;
      if (progress < 1) requestAnimationFrame(step);
    };
    step(start);
  }

  #cancelPending(el) {
    if (this.#pendingDelays.has(el)) {
      clearTimeout(this.#pendingDelays.get(el));
      this.#pendingDelays.delete(el);
    }
  }

  #calculateStagger() {
    const containers = document.querySelectorAll('[data-stagger-children]');
    const ROW_TOLERANCE = 8;

    for (const container of containers) {
      const step = parseInt(container.dataset.staggerChildren, 10) || 100;
      const children = container.querySelectorAll(':scope > * [data-animate]:not(.animated), :scope > [data-animate]:not(.animated)');

      if (children.length < 2) continue;

      const sorted = [...children]
        .map(el => ({ el, top: el.getBoundingClientRect().top, left: el.getBoundingClientRect().left }))
        .sort((a, b) => (a.top - b.top) || (a.left - b.left));

      let rowIndex = -1;
      let lastTop = -Infinity;

      for (const { el, top } of sorted) {
        if (top - lastTop > ROW_TOLERANCE) {
          rowIndex = 0;
          lastTop = top;
        } else {
          rowIndex++;
        }
        el.style.setProperty('--animate-delay', `${rowIndex * step}ms`);
      }
    }
  }

  #watchMutations() {
    let pending = false;

    this.#mutation = new MutationObserver((mutations) => {
      if (pending) return;

      let hasNew = false;
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (node.nodeType === 1 && (node.hasAttribute?.('data-animate') || node.querySelector?.('[data-animate]'))) {
            hasNew = true;
            break;
          }
        }
        if (hasNew) break;
      }
      if (!hasNew) return;

      pending = true;
      requestAnimationFrame(() => {
        TextSplitter.processAll();
        requestAnimationFrame(() => {
          pending = false;
          this.#calculateStagger();
          this.#observeAll();
        });
      });
    });

    this.#mutation.observe(document.body, { childList: true, subtree: true });

    this.#visibilityObserver = new MutationObserver((mutations) => {
      for (const m of mutations) {
        const el = m.target;
        const revealed = m.attributeName === 'open'
          ? el.hasAttribute('open')
          : el.getAttribute('aria-hidden') === 'false';
        if (!revealed) continue;

        requestAnimationFrame(() => {
          const children = el.querySelectorAll('[data-animate]:not(.animated)');
          for (const child of children) {
            if (child.offsetParent !== null) {
              this.#observer?.observe(child);
            }
          }
        });
      }
    });

    this.#visibilityObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-hidden', 'open'],
      subtree: true,
    });
  }

  #watchDesignMode() {
    if (!window.Shopify?.designMode) return;

    document.addEventListener('shopify:section:load', () => {
      setTimeout(() => {
        TextSplitter.processAll();
        this.#calculateStagger();
        this.#observeAll();
      }, 100);
    }, { signal: this.signal });

    document.addEventListener('shopify:section:unload', (e) => {
      const section = e.target;
      if (!section) return;
      section.querySelectorAll('[data-animate]').forEach(el => {
        this.#cancelPending(el);
        this.#observer?.unobserve(el);
        this.#animated.delete(el);
      });
    }, { signal: this.signal });
  }

  #watchResize() {
    let timer;
    window.addEventListener('resize', () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        this.#calculateStagger();
        this.#observeAll();
      }, 250);
    }, { signal: this.signal, passive: true });
  }

  #prefersReduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  #watchReducedMotion() {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    mq.addEventListener('change', (e) => {
      if (e.matches) {
        this.#disable();
        this.#destroy();
      } else {
        this.#enable();
        this.#init();
      }
    }, { signal: this.signal });
  }

  #disable() {
    document.documentElement.classList.add('animations-disabled');
    document.body.classList.remove('animations-ready');
  }

  #enable() {
    document.documentElement.classList.remove('animations-disabled');
  }

  #destroy() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#mutation?.disconnect();
    this.#mutation = null;
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    this.#pendingDelays.forEach(t => clearTimeout(t));
    this.#pendingDelays.clear();
    this.#animated.clear();
    document.body.classList.remove('animations-ready');
  }
}

new AnimationController();
